import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function SearchableContactDropdown({
  contacts,
  selectedContact,
  onContactSelect,
  onAddContact,
  placeholder = "Search contacts...",
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredContacts, setFilteredContacts] = useState(contacts);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Sync searchTerm with selectedContact
  useEffect(() => {
    if (selectedContact) {
      setSearchTerm(selectedContact.contact_name);
    } else {
      setSearchTerm("");
    }
  }, [selectedContact]);

  // Filter contacts based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredContacts(contacts);
    } else {
      const filtered = contacts.filter((contact) => {
        const nameMatch = contact.contact_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const companyMatch =
          contact.company &&
          contact.company.toLowerCase().includes(searchTerm.toLowerCase());
        return nameMatch || companyMatch;
      });
      setFilteredContacts(filtered);
    }
  }, [searchTerm, contacts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle contact selection
  const handleContactSelect = (contact) => {
    onContactSelect(contact);
    setIsOpen(false);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);

    // Only clear selection if input is completely empty
    if (value === "") {
      onContactSelect(null);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Handle Add Contact click
  const handleAddContactClick = () => {
    setIsOpen(false);
    onAddContact();
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          className="w-full px-3 py-2 h-11 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={placeholder}
          autoComplete="off"
        />
        {/* Down Arrow */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-40 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {/* Add Contact Option */}
            <div
              onClick={handleAddContactClick}
              className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 text-blue-600 font-medium"
            >
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add New Contact
              </div>
            </div>

            {/* Contacts List */}
            {filteredContacts.length > 0 ? (
              filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  onClick={() => handleContactSelect(contact)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {contact.contact_name}
                      </div>
                      {contact.company && (
                        <div className="text-sm text-gray-500">
                          {contact.company}
                        </div>
                      )}
                      {contact.title && (
                        <div className="text-xs text-gray-400">
                          {contact.title}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 ml-2">
                      {contact.contact_type}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-gray-500 text-center">
                {searchTerm.trim()
                  ? "No contacts found"
                  : "No contacts available"}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
